import React, {FC, Fragment} from "react";
import useTranslate from "../../modules/core/languages/hooks/useTranslate";
import ApiBooking, { BookingStatus, PaymentStatus } from "../../modules/booking/types/ApiBooking";
import NcImage from "../../modules/ui/shared/NcImage/NcImage";
import {StarIcon} from "@heroicons/react/24/solid";
import {formatNumber} from "../../modules/common/utils/number";
import useCustomerContext from "../../modules/customers/hooks/useCustomerContext";
import Badge from "../../modules/ui/shared/Badge/Badge";
import {TwMainColor} from "../../modules/ui/data/types";
import moment from "moment";
import {SalesChannelEnum} from "../../modules/common/enums/SalesChannelEnum";

export interface CheckoutItemProps {
    item: ApiBooking;
}

// Status -> color mapping
const STATUS_COLOR_MAP: Record<BookingStatus, TwMainColor> = {
    confirmed: 'green',
    pending: 'yellow',
    canceled: 'red',
};

// Payment status metadata (color + translation key)
const PAYMENT_STATUS_META: Record<PaymentStatus, { color: TwMainColor; msgKey: string }> = {
    refunded: { color: 'red', msgKey: 'checkout.paymentStatus.refunded' },
    paid: { color: 'green', msgKey: 'checkout.paymentStatus.paid' },
    unpaid: { color: 'yellow', msgKey: 'checkout.paymentStatus.unpaid' },
    payment_requested: { color: 'purple', msgKey: 'checkout.paymentStatus.payment_requested' },
    payment_scheduled: { color: 'blue', msgKey: 'checkout.paymentStatus.payment_scheduled' },
};

const BookingItem: FC<CheckoutItemProps> = ({
    item,
}) => {
    const t = useTranslate();
    const {languageCode} = useCustomerContext();

    // Use the mapping constants above to determine colors/messages
    const color: TwMainColor = STATUS_COLOR_MAP[item.status] ?? 'gray';
    const paymentMeta = PAYMENT_STATUS_META[item.paymentStatus] ?? { color: 'gray', msgKey: 'checkout.status.notPaid' };
    const colorPaid: TwMainColor = paymentMeta.color;
    const msgPaid: string = t(paymentMeta.msgKey);

    return (
        <>
            <div className="flex flex-col sm:flex-row sm:items-center">
                <div className="flex-shrink-0 w-full sm:w-40">
                    <div className=" aspect-w-4 aspect-h-3 sm:aspect-h-4 rounded-2xl overflow-hidden">
                        <NcImage src={item.hotel.imgContentUrl} />
                    </div>
                </div>
                <div className="sm:px-5 space-y-1 w-[50%]">
                    <div>
                        <span className="text-base font-medium mt-1 block">
                            <div className="flex items-end">
                                {[...Array(item.hotel.stars)].map((value, index) => <StarIcon className="w-[18px] h-[18px] text-orange-500" key={index} />)}
                            </div>
                            {item.eventInitials} {item.city.trName[languageCode]} - {item.hotel.name}
                            {/*<p className="text-sm text-neutral-500 dark:text-neutral-400"><i className="las la-map-marker text-lg ml"></i> {item.hotelAddress}</p>*/}
                        </span>
                    </div>
                    <span className="block  text-sm text-neutral-500 dark:text-neutral-400">
                            {/*{item.room_name}*/}
                        </span>
                    {item.firstRoom && (
                        <>
                            <span className="block  text-sm text-neutral-500 dark:text-neutral-400">
                                    {t("checkout.from")}: {moment(item.firstRoom.arrivalDate).format("DD/MM/YYYY")}
                            </span>
                            <span className="block  text-sm text-neutral-500 dark:text-neutral-400">
                                    {t("checkout.to")}: {moment(item.firstRoom.departureDate).format("DD/MM/YYYY")}
                            </span>
                        </>
                    )}
                    {/*<span className="block  text-sm text-neutral-500 dark:text-neutral-400 flex items-center !mt-2">*/}
                    {/*    <span>{t("hotelDetail.capacity")}</span>*/}
                    {/*    <span className="ml-2">*/}
                    {/*        {[...Array(item.adults)].map((value, index) =>*/}
                    {/*            <i className="las la-user la-2x" key={index + item.item_id}></i>*/}
                    {/*        )}*/}
                    {/*        {[...Array(item.childrens)].map((value, index) =>*/}
                    {/*            <i className="text-2xl las la-user" key={index + item.item_id}></i>*/}
                    {/*        )}*/}
                    {/*    </span>*/}
                    {/*</span>*/}
                </div>
                <div className="py-5 sm:px-5 space-y-3 w-4/6">
                    <div className="flex flex-col space-y-4">
                        {/*<div className="flex justify-between text-neutral-6000 dark:text-neutral-300">*/}
                        {/*    <span>{t("checkout.total_nights")}</span>*/}
                        {/*    <span>{item.total_nights} {t("checkout.nights")}</span>*/}
                        {/*</div>*/}
                        <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                            <span>{t("bookOk.confirmationNumber")}</span>
                            <div>#{item.id}</div>
                        </div>
                        <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                            <span>{t("checkout.status.name")}</span>
                            <div>
                                <Badge name={t(`checkout.status.${item.status}`)} color={color}/>
                                {(item.salesChannel !== SalesChannelEnum.PACKAGE || Number(item.totalBasket) > 0) && (
                                  <Badge name={msgPaid} color={colorPaid} />
                                )}
                            </div>
                        </div>
                        {item.status !== 'canceled' && (item.salesChannel !== SalesChannelEnum.PACKAGE || Number(item.totalBasket) > 0) && (
                            <>
                                <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
                                <div className="flex justify-between font-semibold">
                                    <div className="flex flex-row">
                                        <span>{item.paymentStatus === 'paid' ? t("checkout.total_payed") : t("checkout.total_to_pay")}</span>
                                    </div>
                                    <div>{formatNumber(item?.totalBasket ?? 0)} <span dangerouslySetInnerHTML={{ __html:item.currencySymbol}} /></div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default BookingItem;
